import { create } from 'zustand'
import {
  collection, doc, addDoc, getDocs, updateDoc, deleteDoc,
  query, where, orderBy, serverTimestamp, Timestamp,
} from 'firebase/firestore'
import { db } from '../firebase'
import { useAuthStore } from './auth'
import { USER_ROLES } from '../constants/roles'
import type { Timestamp as FsTimestamp } from 'firebase/firestore'

export interface SessionNote {
  id: string
  orgId: string
  createdBy: string
  createdByName: string
  profileId: string
  profileName: string
  sessionDate: string
  content: string
  clipId?: string
  createdAt: FsTimestamp | null
  updatedAt: FsTimestamp | null
}

function getOrgId(): string {
  const roles = useAuthStore.getState().roles()
  const orgRole = roles.find(r => r.role === USER_ROLES.ORG_ADMIN || r.role === USER_ROLES.STAFF)
  if (!orgRole?.orgId) throw new Error('No org found')
  return orgRole.orgId
}

interface OrgNotesState {
  notes: SessionNote[]
  loading: boolean
  fetchNotes: () => Promise<void>
  addNote: (params: { profileId: string; profileName: string; sessionDate: string; content: string; clipId?: string }) => Promise<void>
  updateNote: (id: string, content: string) => Promise<void>
  deleteNote: (id: string) => Promise<void>
}

export const useOrgNotesStore = create<OrgNotesState>()((set, get) => ({
  notes: [],
  loading: false,

  async fetchNotes() {
    const orgId = getOrgId()
    set({ loading: true })
    try {
      const q = query(
        collection(db, 'org_notes'),
        where('orgId', '==', orgId),
        orderBy('sessionDate', 'desc'),
      )
      const snap = await getDocs(q)
      set({ notes: snap.docs.map(d => ({ id: d.id, ...(d.data() as Omit<SessionNote, 'id'>) })) })
    } catch (err) {
      console.warn('[orgNotes] fetchNotes failed:', err)
    } finally {
      set({ loading: false })
    }
  },

  async addNote({ profileId, profileName, sessionDate, content, clipId }) {
    const auth = useAuthStore.getState()
    const orgId = getOrgId()
    const createdByName = auth.userDoc?.displayName || auth.user?.displayName || 'Team Member'
    const uid = auth.uid()
    if (!uid) throw new Error('Not authenticated')

    const now = Timestamp.now()
    const data = {
      orgId,
      createdBy: uid,
      createdByName,
      profileId,
      profileName,
      sessionDate,
      content: content.trim(),
      ...(clipId ? { clipId } : {}),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }

    try {
      const docRef = await addDoc(collection(db, 'org_notes'), data)
      const newNote: SessionNote = { id: docRef.id, ...data, createdAt: now, updatedAt: now }
      const sorted = [newNote, ...get().notes].sort((a, b) => b.sessionDate.localeCompare(a.sessionDate))
      set({ notes: sorted })
    } catch (err) {
      console.warn('[orgNotes] addNote failed:', err)
      throw err
    }
  },

  async updateNote(id, content) {
    try {
      await updateDoc(doc(db, 'org_notes', id), { content: content.trim(), updatedAt: serverTimestamp() })
      set({
        notes: get().notes.map(n =>
          n.id === id ? { ...n, content: content.trim(), updatedAt: Timestamp.now() } : n
        ),
      })
    } catch (err) {
      console.warn('[orgNotes] updateNote failed:', err)
      throw err
    }
  },

  async deleteNote(id) {
    try {
      await deleteDoc(doc(db, 'org_notes', id))
      set({ notes: get().notes.filter(n => n.id !== id) })
    } catch (err) {
      console.warn('[orgNotes] deleteNote failed:', err)
      throw err
    }
  },
}))
